/** @returns The configured Maps API key. */
const mapKey = () =>
  document.getElementById('google-maps-script').dataset.mapKey

//
// Geometry
//

/** @private */
const defaultLatLngSelector = ({ lat, lng }) => new google.maps.LatLng(lat, lng)

/**
 * Sorts a list with items nearest to the latitude and longitude coming first.
 *
 * @param {object} origin object like {lat: _, lng: _}
 * @param {array} list list of objects to sort
 * @param {Function} latLngSelector convert an item in `list` to an instance of
 *    Google's LatLng class. This is optional, and defaults to passing the lat
 *    and lng properties into the LatLng constructor.
 * @returns {array} list of objects in ascending order based on how close they
 *    are to the origin.
 */
function sortByNearestTo(origin, list, latLngSelector = defaultLatLngSelector) {
  const { spherical } = google.maps.geometry
  const originLatLng = latLngSelector(origin)

  const distanceFromOrigin = latLng =>
    spherical.computeDistanceBetween(originLatLng, latLng)

  return list.sort((lhs, rhs) => {
    const lhsDistance = distanceFromOrigin(latLngSelector(lhs))
    const rhsDistance = distanceFromOrigin(latLngSelector(rhs))

    return lhsDistance - rhsDistance
  })
}

/**
 * Returns a value indicating whether location is within some distance (in
 * miles) of the origin.
 *
 * This is handy if you want to re-center the map to a BonChon location near the
 * user's search term. When this returns true, you know you have a location
 * nearby that you can use.
 *
 * @param {Number} miles
 * @param {object} origin
 * @param {object} location
 * @param {Function} latLngSelector converts a location to latitude/longitude.
 * @returns {boolean} a value indicating whether the location is within some
 *    amount of miles the origin
 */
function isInRangeOf(
  miles,
  origin,
  location,
  latLngSelector = defaultLatLngSelector
) {
  const { computeDistanceBetween } = google.maps.geometry.spherical
  const originLatLng = latLngSelector(origin)
  const locationLatLng = latLngSelector(location)

  // JS floating point math is bad, this should possibly be re-written to be
  // precise.
  const milesInMeters = miles * 1609.34
  const distanceInMeters = computeDistanceBetween(originLatLng, locationLatLng)
  const res = distanceInMeters < milesInMeters
  return res
}

//
// Geocoding (& Reverse Geocoding)
//

/**
 * Hits the Geocoding API provided by Google.
 *
 * We can reverse geocode the user's current location (i.e., lat/lng) to show
 * them where they are in plain language. Use 'latLng' for `geocodeType`.
 *
 * We can geocode input from a user (i.e., a string) to get the lat/lng closest
 * to the user's search query. Use 'address' for `geocodeType`.
 *
 * @param {string} geocodeType may be one of two values: 'address' or 'latLng'.
 * @param {string | object} value a search query or the user's lat/lng.
 */
async function asyncGeocodeByType(geocodeType, value) {
  const { Geocoder } = google.maps

  const geocoder = new Geocoder()
  const params = { [geocodeType]: value }

  return new Promise((resolve, reject) => {
    try {
      geocoder.geocode(params, (results, status) => {
        if (status === 'OK') {
          const first = results[0]
          const latLng = first && first.geometry && first.geometry.location

          return resolve({
            ok: true,
            // toJSON() maps to a plain object.
            latLng: latLng.toJSON(),
            first,
            results
          })
        }

        return reject({ ok: false, error: status, data: results })
      })
    } catch (error) {
      return reject({ ok: false, error: 'Exception', data: error })
    }
  })
}

export { asyncGeocodeByType, sortByNearestTo, isInRangeOf }



// WEBPACK FOOTER //
// ./frontend/resources/assets/js/utils/locationUtils.js